import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Sparkles, TrendingUp, AlertTriangle, CheckCircle2, Scale } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import PageArrival from '../components/PageArrival';
import CareerTargetLock from '../components/CareerTargetLock';
import KnownFactsStrip from '../components/KnownFactsStrip';
import { useFPContext } from '../context/FuturePathContext';
import { Sparkles as A1, Route as A2, Wallet as A3, Scale as A4, Clock as A5, AlertTriangle as A6 } from 'lucide-react';


const API = `${process.env.REACT_APP_BACKEND_URL}/api/future-self`;
const ROI_API = `${process.env.REACT_APP_BACKEND_URL}/api/learning-roi`;

const money = (n) => (n || n === 0 ? `$${Math.round(n).toLocaleString()}` : '-');

export default function FutureSelfPage() {
  const [arrived, setArrived] = useState(false);
  const { getAuthHeader, user } = useAuth();
  const { mayAsk, askInstead } = useFPContext();
  const [d, setD] = useState(null);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    target_career: '', current_income: '', credential_type: 'short_certificate', borrowing: '',
    horizon_years: 5, state: '', would_relocate: false, hours_per_week: '', family_obligations: 'none',
  });
  const [occQuery, setOccQuery] = useState('');
  const [occResults, setOccResults] = useState([]);

  const run = useCallback(async (payload) => {
    setBusy(true);
    try {
      const res = await axios.post(API, payload, getAuthHeader());
      setD(res.data);
    } catch { toast.error('Could not build your 2030 scenarios.'); }
    finally { setBusy(false); setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => {
    (async () => {
      const o = await axios.get(`${ROI_API}/options`, getAuthHeader());
      setTypes(o.data.credential_types);
      await run({});
    })();
  }, [getAuthHeader, run]);

  const searchOccupations = async (q) => {
    setOccQuery(q);
    setForm(f => ({ ...f, target_career: q }));
    if (q.trim().length < 2) { setOccResults([]); return; }
    const r = await axios.get(`${API}/occupations?q=${encodeURIComponent(q)}`, getAuthHeader());
    setOccResults(r.data.results || []);
  };

  const submit = (overrides = {}) => run({
    target_career: form.target_career || undefined,
    current_income: form.current_income !== '' ? Number(form.current_income) : undefined,
    credential_type: form.credential_type,
    borrowing: form.borrowing !== '' ? Number(form.borrowing) : undefined,
    horizon_years: form.horizon_years,
    state: form.state || undefined,
    would_relocate: form.would_relocate,
    hours_per_week: form.hours_per_week !== '' ? Number(form.hours_per_week) : undefined,
    family_obligations: form.family_obligations,
    ...overrides,
  });

  if (loading) {
    return <AppLayout><div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div></AppLayout>;
  }

  const max = Math.max(...(d.paths || []).map(p => p.income_2030 || 0), 1);

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="future-self-arrival"
        eyebrow="Meet My Future Self"
        title="Four versions of you. Same starting point."
        subtitle="Stay where you are. Take the funded route. Borrow for it. Or earn while you learn. Each one played forward to the same year, income, debt carried, time spent, and whether it clears the life you designed."
        accent="#a78bfa"
        second="#22d3ee"
        ctaLabel="Meet my future self"
        narration={"This is Meet My Future Self. Instead of one prediction, you get four versions of the same person: staying put, taking a funded route, borrowing for it, and earning while you learn. Each one runs forward on your field's own wage trend and shows the income you'd have, the debt you'd carry, the time it costs, and whether it clears the number your life takes. Every version also names its tradeoffs and what would have to be true for it to work. These are models, not prophecies, but they're built on your numbers."}
        facts={[
          { icon: A2, head: 'Four routes, side by side', sub: 'Stay, funded, borrowed, earn-while-you-learn' },
          { icon: A3, head: 'Income and debt', sub: 'Both ends of the trade, not just the upside' },
          { icon: A4, head: 'Judged against your life', sub: 'Does it clear the number you set?' },
        ]}
        modulesTitle="What each version shows"
        modules={[
          { icon: A3, label: 'Income at the horizon', line: 'Drifted at the wage trend of your own field, not a flat guess.', hue: '#34d399' },
          { icon: A6, label: 'Debt carried', line: 'What you\u2019d still owe, and what it costs you monthly.', hue: '#f472b6' },
          { icon: A5, label: 'Time cost', line: 'Years in, plus the earnings you give up while you study.', hue: '#fbbf24' },
          { icon: A4, label: 'Clears your floor?', line: 'A straight answer against the income your life takes.', hue: '#22d3ee' },
          { icon: A1, label: 'Tradeoffs named', line: 'What each route costs you that isn\u2019t money.', hue: '#a78bfa' },
          { icon: A2, label: 'What has to be true', line: 'The assumptions each version depends on, stated plainly.', hue: '#60a5fa' },
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <PageHeader
        title={`Meet Yourself in ${d.horizon_year}`}
        description={`Four versions of you in ${d.horizon_year}, ${d.years_out} years out. Same person, different decisions, with the tradeoffs written down.`}
      />

      <GlassCard className="mb-6 fp-rail" data-testid="future-self-inputs">
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <span className="text-[11px] uppercase tracking-wider text-slate-300 mr-1">How far out</span>
          {(d.horizon_options || []).map(o => (
            <button key={o.years} data-testid={`horizon-${o.years}`}
              onClick={() => { setForm(f => ({ ...f, horizon_years: o.years })); submit({ horizon_years: o.years }); }}
              className="fp-pill"
              style={form.horizon_years === o.years ? { borderColor: '#22d3ee', background: 'rgba(34,211,238,.14)', color: '#fff' } : undefined}>
              {o.label}
            </button>
          ))}
        </div>

        <KnownFactsStrip className="mb-4" keys={['state', 'geographic_flexibility', 'income_floor', 'education_stage']} />

        <CareerTargetLock className="mb-4" previewing={form.target_career}
          onPreview={(t) => { setForm(f => ({ ...f, target_career: t })); setOccQuery(t); submit({ target_career: t || undefined }); }} />

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-300 mb-1.5">
              {mayAsk('current_income') ? 'Income today' : (askInstead('current_income') || 'Income you are aiming for')}
            </label>
            <Input type="number" value={form.current_income} onChange={e => setForm({ ...form, current_income: e.target.value })}
              placeholder={mayAsk('current_income') ? (d.current?.income ? String(d.current.income) : '45000') : 'Leave blank if you are not earning yet'}
              data-testid="fs-income-input" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-300 mb-1.5">Route you'd take</label>
            <select value={form.credential_type} onChange={e => setForm({ ...form, credential_type: e.target.value })}
              className="input-dark w-full" data-testid="fs-credential-select">
              {types.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-300 mb-1.5">If you borrowed</label>
            <Input type="number" value={form.borrowing} onChange={e => setForm({ ...form, borrowing: e.target.value })}
              placeholder="10000" data-testid="fs-borrow-input" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-300 mb-1.5">State you live in</label>
            <Input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })}
              placeholder="New Jersey" data-testid="fs-state-input" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-300 mb-1.5">Hours a week you can give</label>
            <Input type="number" value={form.hours_per_week} onChange={e => setForm({ ...form, hours_per_week: e.target.value })}
              placeholder="10" data-testid="fs-hours-input" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-300 mb-1.5">Family obligations</label>
            <select value={form.family_obligations} onChange={e => setForm({ ...form, family_obligations: e.target.value })}
              className="input-dark w-full" data-testid="fs-family-select">
              <option value="none">Just me</option>
              <option value="some">Some, partner or older kids</option>
              <option value="heavy">Heavy, young kids or caregiving</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-xs text-slate-300 pb-2">
              <input type="checkbox" checked={form.would_relocate} data-testid="fs-relocate-check"
                onChange={e => setForm({ ...form, would_relocate: e.target.checked })} />
              I'd relocate for the right role
            </label>
          </div>
        </div>
        <Button className="btn-primary mt-4" disabled={busy} data-testid="fs-run-btn" onClick={() => submit()}>
          {busy ? 'Modelling…' : <><Sparkles className="w-4 h-4 mr-2" />Show me {d.horizon_year}</>}
        </Button>
      </GlassCard>

      {/* Today */}
      <GlassCard className="mb-6" data-testid="future-self-today">
        <div className="flex flex-wrap items-center gap-6">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-slate-300">Today</p>
            <p className="text-lg font-semibold text-white">{d.current.role}</p>
            <p className="text-xs text-slate-300">
              {d.current.income ? `${money(d.current.income)} · ` : ''}{d.current.skills_on_file} skills on file
              {d.current.archetypes?.length ? ` · ${d.current.archetypes.join(' · ')}` : ''}
            </p>
          </div>
          {d.target?.occupation && (
            <div className="pl-6 border-l border-slate-700">
              <p className="text-[11px] uppercase tracking-wider text-slate-300">Destination</p>
              <p className="text-lg font-semibold text-white">{d.target.occupation}</p>
              <p className="text-xs text-slate-300">
                Median today {money(d.target.median_today)} → {d.horizon_year} {money(d.target.median_2030)}
              </p>
            </div>
          )}
          {d.income_floor && (
            <div className="pl-6 border-l border-slate-700">
              <p className="text-[11px] uppercase tracking-wider text-slate-300">Your income floor</p>
              <p className="text-lg font-semibold text-white fp-num">{money(d.income_floor)}</p>
            </div>
          )}
        </div>
      </GlassCard>

      {/* Paths */}
      <div className="grid md:grid-cols-2 gap-5" data-testid="future-paths">
        {(d.paths || []).map(p => (
          <div key={p.key} data-testid={`path-${p.key}`}
            className="relative overflow-hidden rounded-2xl border p-5"
            style={{
              borderColor: `${p.hue}55`,
              background: `linear-gradient(160deg, ${p.hue}17, rgba(8,20,38,.72) 62%)`,
              boxShadow: `0 18px 44px rgba(3,12,26,.45), 0 0 28px ${p.hue}22`,
            }}>
            <span className="absolute inset-x-0 top-0 h-[3px]" style={{ background: p.hue }} />
            <span className="absolute -top-24 -right-24 w-56 h-56 rounded-full blur-3xl opacity-25" style={{ background: p.hue }} />

            <div className="relative">
              <h3 className="text-base md:text-lg font-semibold text-white">{p.title}</h3>
              <p className="text-xs text-slate-300/80 mt-1">{p.blurb}</p>

              <div className="grid grid-cols-3 gap-3 mt-5">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-300">{p.horizon_year || d.horizon_year} income</p>
                  <p className="text-xl font-bold text-white fp-num">{money(p.income_2030)}</p>
                  {p.vs_today != null && (
                    <p className={`text-[11px] fp-num ${p.vs_today >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                      {p.vs_today >= 0 ? '+' : ''}{money(p.vs_today)} vs today
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-300">Debt carried</p>
                  <p className={`text-xl font-bold fp-num ${p.debt_2030 ? 'text-amber-300' : 'text-emerald-300'}`}>{money(p.debt_2030)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-300">AI exposure</p>
                  <p className="text-xl font-bold text-white fp-num">{p.ai_exposure ?? '-'}</p>
                </div>
              </div>

              <div className="fp-meter mt-4">
                <div className="fp-meter-fill" style={{
                  width: `${Math.round(((p.income_2030 || 0) / max) * 100)}%`,
                  background: p.hue, boxShadow: `0 0 12px ${p.hue}99`,
                }} />
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                <span className="fp-pill" style={{ borderColor: `${p.hue}55` }}>{p.time_cost}</span>
                {p.outlook && <span className="fp-pill" style={{ borderColor: `${p.hue}55` }}>{p.outlook}</span>}
                {p.clears_floor != null && (
                  <span className="fp-pill" style={{
                    borderColor: p.clears_floor ? '#34d39988' : '#f8717188',
                    color: p.clears_floor ? '#6ee7b7' : '#fca5a5',
                  }}>
                    {p.clears_floor ? 'Clears your income floor' : 'Below your income floor'}
                  </span>
                )}
              </div>

              <div className="mt-4">
                <p className="text-[10px] uppercase tracking-[0.16em] text-slate-300 flex items-center gap-1.5">
                  <Scale className="w-3 h-3" /> Tradeoffs
                </p>
                <ul className="mt-2 space-y-1">
                  {p.tradeoffs.map((t, i) => (
                    <li key={i} className="text-xs text-slate-300 flex gap-2"><span style={{ color: p.hue }}>•</span>{t}</li>
                  ))}
                </ul>
              </div>

              <div className="mt-4">
                <p className="text-[10px] uppercase tracking-[0.16em] text-slate-300 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3 h-3" /> What has to be true
                </p>
                <ul className="mt-2 space-y-1">
                  {p.must_be_true.map((t, i) => (
                    <li key={i} className="text-xs text-slate-300/90 flex gap-2"><span className="text-slate-300">-</span>{t}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>

      {(d.signals || []).length > 0 && (
        <GlassCard className="mt-6" data-testid="future-self-signals">
          <h3 className="text-base md:text-lg font-semibold text-white flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-cyan-300" /> What applies to every path
          </h3>
          <ul className="space-y-2">
            {d.signals.map((s, i) => (
              <li key={i} className="flex gap-2 text-sm text-slate-300"><AlertTriangle className="w-3.5 h-3.5 text-amber-300 mt-0.5 shrink-0" />{s}</li>
            ))}
          </ul>
        </GlassCard>
      )}

      <p className="text-xs text-slate-300 mt-6">{d.disclosure} Sources: {d.sources.join(' · ')}.</p>
    </AppLayout>
  );
}
